import { Banner, useAppInitTask, useIfUser } from '@if/mui'
import { Box } from '@mui/material'
import { useMerchantByIdQuery } from '@src/data/api/merchants-api/merchants-api'
import { Merchant } from '@src/types/Merchant'
import { createContext, useContext, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

const MerchantContext = createContext<Merchant | null>(null)

/**
 * The authenticated user's own merchant, resolved once at boot. Non-null — the
 * provider is a blocking gate, so any component below it reads a loaded merchant
 * (no `isLoading`, no optional chaining on the merchant itself). Throws outside
 * `<MerchantProvider>`.
 */
export const useMerchant = (): Merchant => {
  const merchant = useContext(MerchantContext)
  if (!merchant) throw new Error('useMerchant must be used within <MerchantProvider>.')
  return merchant
}

/**
 * Boot-level loader for the current user's merchant. Mounted below the auth gate
 * (needs `useIfUser`) and under the design-system `AppLoader`: it holds the shared
 * boot splash via `useAppInitTask` until the merchant fetch settles, so the app
 * shell never renders against a half-loaded merchant. The merchant is essential
 * (the whole dashboard is scoped to it), so a load failure surfaces a page error
 * rather than opening the app with a null merchant.
 */
const MerchantProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const user = useIfUser()
  const { data: merchant, isPending, isError } = useMerchantByIdQuery(user.merchantId)

  // Hold the boot splash until the merchant settles (success or error).
  useAppInitTask(!isPending, 'merchant')

  if (isPending) return null
  if (isError || !merchant) {
    return (
      <Box sx={{ p: 4 }}>
        <Banner severity="error">
          {t('errors.merchantLoadFailed', 'Unable to load your merchant. Please try again later.')}
        </Banner>
      </Box>
    )
  }

  return <MerchantContext.Provider value={merchant}>{children}</MerchantContext.Provider>
}

export default MerchantProvider
